import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertOctagon,
    AlertTriangle,
    ArrowRight,
    BookOpen,
    Bookmark,
    Bot,
    CalendarDays,
    Check,
    ChevronDown,
    ClipboardCheck,
    Copy,
    ExternalLink,
    Filter,
    Flame,
    Globe2,
    Loader2,
    Magnet,
    RefreshCw,
    Search,
    ShieldAlert,
    Sparkles,
    Star,
    UserCheck,
    X
} from 'lucide-react';
import { CompareFloatingButton, ComparisonModal, CopilotDrawer, DiscoveryModes, InsightsBar } from './HopeLabIntelligence';
import { hopeLabCoverUrl } from './coverUrl';

const FALLBACK_DEFAULTS = {
    yearMin: 2015,
    yearMax: new Date().getFullYear(),
    excludedStudios: ['Podium Audio', 'Aethon Audio', 'Audible Studios', 'Blackstone Publishing', 'Mountaindale Press'],
    preferredStudios: ['Tantor Audio', 'Royal Guard Publishing', 'Soundbooth Theater'],
    genres: ['LitRPG', 'Post Apocalyptic', 'Sci-Fi', 'Fantasy', 'Thriller', 'Zombies'],
    maxResults: 18
};

function canonical(value) {
    return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function timestampLabel(value) {
    if (!value) return '';
    if (typeof value === 'string') return new Date(value).toLocaleString('vi-VN');
    const seconds = value.seconds || value._seconds;
    return seconds ? new Date(seconds * 1000).toLocaleString('vi-VN') : '';
}

function addDays(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

function isoDate(date) {
    return date.toISOString().slice(0, 10);
}

function pickSuggestedDate(channelName, tasks) {
    const channelTasks = (tasks || []).filter(task => task.channel === channelName && task.date);
    let candidate = addDays(new Date(), 1);
    for (let attempts = 0; attempts < 90; attempts += 1) {
        const dateString = isoDate(candidate);
        const tooClose = channelTasks.some(task => {
            const diff = Math.abs(new Date(`${task.date}T00:00:00`) - new Date(`${dateString}T00:00:00`)) / 86400000;
            return diff < 4;
        });
        if (!tooClose) return dateString;
        candidate = addDays(candidate, 1);
    }
    return isoDate(addDays(new Date(), 1));
}

function Avatar({ person, size = 'md' }) {
    const name = person?.name || 'HopeTeam';
    return (
        <span className={`hopelab-avatar hopelab-avatar-${size}`}>
            {person?.avatar ? <img src={person.avatar} alt={name} /> : name.slice(0, 2).toUpperCase()}
        </span>
    );
}

function ScoreRing({ score }) {
    const value = Math.max(0, Math.min(100, Number(score) || 0));
    return (
        <div className="hopelab-score-ring" style={{ '--hopelab-score': `${value * 3.6}deg` }}>
            <span>{value}</span><small>/100</small>
        </div>
    );
}

function SmartBookCover({ cover, title }) {
    const [shape, setShape] = useState('portrait');
    if (!cover) return <BookOpen size={34} />;
    const src = hopeLabCoverUrl(cover);
    const handleLoad = event => {
        const ratio = event.currentTarget.naturalWidth / Math.max(1, event.currentTarget.naturalHeight);
        setShape(ratio > 1.12 ? 'wide' : ratio > 0.82 ? 'square' : ratio < 0.55 ? 'tall' : 'portrait');
    };
    return (
        <div className={`hopelab-cover-media is-${shape}`}>
            <img className="hopelab-cover-backdrop" src={src} alt="" aria-hidden="true" loading="lazy" />
            <img className="hopelab-cover-main" src={src} alt={title} loading="lazy" onLoad={handleLoad} />
        </div>
    );
}

function isBktChannel(channel) {
    return canonical(channel?.name).split(' ').includes('bkt');
}

function TaskModal({ book, role, currentUserData, staff, channels, tasks, onClose, onCreate, saving }) {
    const assignableStaff = role === 'member' ? [currentUserData].filter(Boolean) : staff;
    const [assigneeId, setAssigneeId] = useState(currentUserData?.id || assignableStaff[0]?.id || '');
    const managedChannels = useMemo(() => {
        const assignee = assignableStaff.find(person => person.id === assigneeId);
        return (channels || []).filter(channel => canonical(channel.manager) === canonical(assignee?.name));
    }, [assignableStaff, assigneeId, channels]);
    const allowedChannels = useMemo(() => managedChannels.filter(channel => !isBktChannel(channel) || book.preferredStudio), [book.preferredStudio, managedChannels]);
    const suggestedChannel = useMemo(() => {
        const bookTerms = canonical([...(book.categories || []), ...(book.keywords || [])].join(' '));
        return allowedChannels.find(channel => canonical(channel.genre).split(' ').some(term => term.length > 3 && bookTerms.includes(term))) || allowedChannels[0];
    }, [allowedChannels, book]);
    const [channelId, setChannelId] = useState('');
    const activeChannelId = channelId || suggestedChannel?.id || '';
    const activeChannel = allowedChannels.find(channel => channel.id === activeChannelId);
    const [date, setDate] = useState('');
    const suggestedDate = date || pickSuggestedDate(activeChannel?.name, tasks);

    return (
        <div className="hopelab-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="hopelab-task-modal">
                <div className="hopelab-task-modal-header">
                    <div><span>AUTO TASK</span><h3>Đưa sách vào lịch đăng</h3></div>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                <div className="hopelab-task-book">
                    {book.cover ? <img src={hopeLabCoverUrl(book.cover)} alt="" /> : <BookOpen size={28} />}
                    <div><strong>{book.title}</strong><small>{book.author || 'Chưa xác định tác giả'}</small></div>
                </div>
                <div className="hopelab-task-form">
                    <label>
                        <span>Người phụ trách</span>
                        <select value={assigneeId} disabled={role === 'member'} onChange={event => { setAssigneeId(event.target.value); setChannelId(''); setDate(''); }}>
                            {assignableStaff.map(person => <option key={person.id} value={person.id}>{person.name}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>Kênh phù hợp</span>
                        <select value={activeChannelId} onChange={event => { setChannelId(event.target.value); setDate(''); }}>
                            {allowedChannels.length ? allowedChannels.map(channel => <option key={channel.id} value={channel.id}>{channel.name} · {channel.genre || 'General'}</option>) : <option value="">Không có kênh phù hợp với policy</option>}
                        </select>
                    </label>
                    <label>
                        <span>Ngày đăng gợi ý</span>
                        <input type="date" value={suggestedDate} onChange={event => setDate(event.target.value)} />
                        <small>Tự tránh các task cùng kênh cách nhau dưới 4 ngày.</small>
                        {!book.preferredStudio && managedChannels.some(isBktChannel) && <small className="hopelab-policy-warning">Kênh BKT đã bị loại khỏi danh sách vì sách không thuộc publisher whitelist.</small>}
                    </label>
                </div>
                <div className="hopelab-task-modal-actions">
                    <button className="hopelab-secondary-button" onClick={onClose}>Hủy</button>
                    <button
                        className="hopelab-primary-button"
                        disabled={saving || !activeChannelId || !suggestedDate}
                        onClick={() => onCreate({ assigneeId, channelId: activeChannelId, date: suggestedDate })}
                    >
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <ClipboardCheck size={15} />}
                        Tạo task ngay
                    </button>
                </div>
            </div>
        </div>
    );
}

function SeriesModal({ book, loading, error, onClose, onRefresh, onCopyAll, onDownload, onCopyOne, isAdmin }) {
    const seriesBooks = book.seriesBooks || [];
    const magnetCount = seriesBooks.filter(item => item.magnet).length;

    return (
        <div className="hopelab-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="hopelab-series-modal" role="dialog" aria-modal="true" aria-labelledby="hopelab-series-title">
                <div className="hopelab-series-modal-header">
                    <div>
                        <span>FULL SERIES · {seriesBooks.length || book.seriesCount || 0} VOLUME</span>
                        <h3 id="hopelab-series-title">{book.seriesTitle || book.title}</h3>
                        <p>Mở từng cuốn hoặc sao chép toàn bộ magnet để dán một lần vào qBittorrent/uTorrent.</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Đóng danh sách series"><X size={20} /></button>
                </div>

                <div className="hopelab-series-modal-tools">
                    <button type="button" disabled={loading} onClick={onRefresh}>
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        {book.fullSeriesLoaded ? 'Làm mới danh sách' : 'Lấy toàn bộ series'}
                    </button>
                    <button type="button" disabled={!magnetCount} onClick={onCopyAll}>
                        <Copy size={14} /> Copy {magnetCount} magnet
                    </button>
                    <button type="button" disabled={!seriesBooks.length} onClick={onDownload}>
                        <ExternalLink size={14} /> Tải file URL + magnet
                    </button>
                    <span>{loading ? 'Đang xác minh metadata từng cuốn…' : `${magnetCount}/${seriesBooks.length} cuốn có magnet`}</span>
                </div>

                {error && <div className="hopelab-alert is-error"><AlertOctagon size={16} /><span>{error}</span></div>}

                <div className="hopelab-series-modal-body">
                    {!seriesBooks.length && loading && (
                        <div className="hopelab-series-empty"><Loader2 size={22} className="animate-spin" /> Đang lấy danh sách đầy đủ…</div>
                    )}
                    {!seriesBooks.length && !loading && (
                        <div className="hopelab-series-empty">Chưa tìm thấy volume nào. Hãy thử làm mới danh sách.</div>
                    )}
                    {seriesBooks.map((item, index) => {
                        const publisher = item.audioPublisher || item.printPublisher || item.publisher;
                        const publisherType = item.audioPublisher ? 'Audio publisher' : item.printPublisher ? 'Print publisher' : 'Publisher';
                        return (
                            <div key={item.id || `${item.title}-${index}`} className={`hopelab-series-volume ${item.excludedStudio ? 'is-risk' : ''}`}>
                                <div className="hopelab-series-volume-cover">
                                    {item.cover ? <img src={hopeLabCoverUrl(item.cover)} alt="" loading="lazy" /> : <BookOpen size={22} />}
                                    <span>{index + 1}</span>
                                </div>
                                <div className="hopelab-series-volume-info">
                                    <strong>{item.title}</strong>
                                    <div className="hopelab-series-metadata">
                                        <span><b>Năm:</b> {item.releaseYear || 'Chưa xác minh'}{item.yearSource && <em>{item.yearSource}</em>}</span>
                                        <span title={publisher || ''}><b>{publisherType}:</b> {publisher || 'Chưa xác minh'}{item.publisherSource && <em>{item.publisherSource}</em>}</span>
                                        <span><b>Rating:</b> {item.rating ? `${Number(item.rating).toFixed(1)} (${item.ratingsCount || 0})` : 'Chưa có'}</span>
                                    </div>
                                    {item.excludedStudio && <small><ShieldAlert size={12} /> Publisher nằm trong blacklist</small>}
                                </div>
                                <div className="hopelab-series-volume-actions">
                                    {item.abbUrl && <a href={item.abbUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> AudiobookBay</a>}
                                    {isAdmin && item.audiobbUrl && <a href={item.audiobbUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> AudioBB</a>}
                                    {isAdmin && (item.fileHostLinks || []).map(link => <a key={link.url} href={link.url} target="_blank" rel="noreferrer"><ExternalLink size={13} /> {link.label}</a>)}
                                    {item.magnet ? (
                                        <>
                                            <a href={item.magnet}><Magnet size={13} /> Mở torrent</a>
                                            <button type="button" onClick={() => onCopyOne(item)}><Copy size={13} /> Copy magnet</button>
                                        </>
                                    ) : <span>Chưa có magnet</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="hopelab-series-modal-footer">
                    <span>Metadata có nhãn nguồn; Google Books là publisher bản in, không thay thế audio publisher.</span>
                    <button type="button" className="hopelab-secondary-button" onClick={onClose}>Đóng</button>
                </div>
            </div>
        </div>
    );
}

export default function HopeLabView({ firebaseUser, userRole, currentUserData, staff = [], channels = [], tasks = [], onNavigate }) {
    const [defaults, setDefaults] = useState(FALLBACK_DEFAULTS);
    const [query, setQuery] = useState('');
    const [genres, setGenres] = useState(['LitRPG']);
    const [books, setBooks] = useState([]);
    const [savedBooks, setSavedBooks] = useState([]);
    const [showSavedOnly, setShowSavedOnly] = useState(false);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [aiConfigured, setAiConfigured] = useState(false);
    const [model, setModel] = useState('');
    const [yearMin, setYearMin] = useState(FALLBACK_DEFAULTS.yearMin);
    const [yearMax, setYearMax] = useState(FALLBACK_DEFAULTS.yearMax);
    const [hideBlacklisted, setHideBlacklisted] = useState(true);
    const [channelPreset, setChannelPreset] = useState('standard');
    const [sortBy, setSortBy] = useState('score');
    const [studioFilter, setStudioFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [busyBookId, setBusyBookId] = useState('');
    const [taskBook, setTaskBook] = useState(null);
    const [taskSaving, setTaskSaving] = useState(false);
    const [loadingSeriesId, setLoadingSeriesId] = useState('');
    const [seriesModalId, setSeriesModalId] = useState('');
    const [seriesError, setSeriesError] = useState('');
    const [verifyingPublishers, setVerifyingPublishers] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisDone, setAnalysisDone] = useState(false);
    const [sourceStatus, setSourceStatus] = useState({ abb: null, audiobb: null });
    const [freshReadyAt, setFreshReadyAt] = useState(0);
    const [freshCooldownSeconds, setFreshCooldownSeconds] = useState(300);
    const [clockNow, setClockNow] = useState(Date.now());
    const [discoveryMode, setDiscoveryMode] = useState('latest');
    const [independentOnly, setIndependentOnly] = useState(false);
    const [olderThanFive, setOlderThanFive] = useState(false);
    const [completedOnly, setCompletedOnly] = useState(false);
    const [ratingMin, setRatingMin] = useState(0);
    const [reviewsMin, setReviewsMin] = useState(0);
    const [reviewsMax, setReviewsMax] = useState(0);
    const [compareIds, setCompareIds] = useState([]);
    const [comparisonOpen, setComparisonOpen] = useState(false);
    const [copilotOpen, setCopilotOpen] = useState(false);
    const activeBooks = showSavedOnly ? savedBooks : books;
    const activeBookIds = useMemo(() => activeBooks.map(book => book.id).filter(Boolean).slice(0, 40).join(','), [activeBooks]);

    const apiFetch = useCallback(async (url, options = {}) => {
        if (!firebaseUser) throw new Error('Vui lòng đăng nhập lại để tiếp tục.');
        const send = async forceRefresh => {
            const token = await firebaseUser.getIdToken(forceRefresh);
            return fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    ...(options.headers || {})
                }
            });
        };
        let response = await send(false);
        if (response.status === 401) response = await send(true);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            const requestError = new Error(payload.error || (response.status === 401 ? 'Phiên đăng nhập không còn hợp lệ. Vui lòng đăng nhập lại.' : `HTTP ${response.status}`));
            requestError.retryAfterSeconds = Number(payload.retryAfterSeconds) || 0;
            throw requestError;
        }
        return payload;
    }, [firebaseUser]);

    useEffect(() => {
        if (!firebaseUser) return undefined;
        let active = true;
        apiFetch('/api/hopelab/action?saved=1')
            .then(payload => { if (active) setSavedBooks(Array.isArray(payload.books) ? payload.books : []); })
            .catch(() => {});
        return () => { active = false; };
    }, [apiFetch, firebaseUser]);

    useEffect(() => {
        if (!firebaseUser) return undefined;
        let active = true;
        setError('');
        apiFetch('/api/hopelab/research').then(payload => {
            if (!active) return;
            const nextDefaults = { ...FALLBACK_DEFAULTS, ...(payload.defaults || {}) };
            setDefaults(nextDefaults);
            setGenres(current => current.length === 1 && nextDefaults.genres.includes(current[0]) ? current : [nextDefaults.genres[0] || 'LitRPG']);
            setYearMin(nextDefaults.yearMin);
            setYearMax(nextDefaults.yearMax);
            setAiConfigured(Boolean(payload.aiConfigured));
            setModel(payload.model || '');
            setSourceStatus(payload.sources || { abb: null, audiobb: null });
            setFreshReadyAt(payload.freshResearch?.nextAt ? Date.parse(payload.freshResearch.nextAt) : 0);
            setFreshCooldownSeconds(Number(payload.freshResearch?.cooldownSeconds) || 300);
        }).catch(requestError => active && setError(requestError.message));
        return () => { active = false; };
    }, [apiFetch, firebaseUser]);

    useEffect(() => {
        if (!firebaseUser) return undefined;
        let active = true;
        const refreshSourceStatus = () => apiFetch('/api/hopelab/research?action=status')
            .then(payload => { if (active) setSourceStatus(payload.sources || { abb: null, audiobb: null }); })
            .catch(() => {});
        const timer = window.setInterval(refreshSourceStatus, 600000);
        return () => { active = false; window.clearInterval(timer); };
    }, [apiFetch, firebaseUser]);

    useEffect(() => {
        if (!freshReadyAt || freshReadyAt <= Date.now()) return undefined;
        setClockNow(Date.now());
        const timer = window.setInterval(() => setClockNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [freshReadyAt]);

    const freshSecondsLeft = Math.max(0, Math.ceil((freshReadyAt - clockNow) / 1000));
    const freshCountdown = freshSecondsLeft
        ? `${String(Math.floor(freshSecondsLeft / 60)).padStart(2, '0')}:${String(freshSecondsLeft % 60).padStart(2, '0')}`
        : '';

    const mergeState = useCallback(state => {
        if (!state?.id) return;
        setBooks(current => current.map(book => book.id === state.id ? { ...book, ...state } : book));
        setSavedBooks(current => current.map(book => book.id === state.id ? { ...book, ...state } : book));
    }, []);

    useEffect(() => {
        if (!activeBookIds) return undefined;
        const refreshStates = () => apiFetch(`/api/hopelab/action?ids=${encodeURIComponent(activeBookIds)}`)
            .then(payload => {
                const stateMap = new Map((payload.states || []).map(state => [state.id, state]));
                setBooks(current => current.map(book => stateMap.has(book.id) ? { ...book, ...stateMap.get(book.id) } : book));
                setSavedBooks(current => current.map(book => stateMap.has(book.id) ? { ...book, ...stateMap.get(book.id) } : book));
            })
            .catch(() => {});
        refreshStates();
        const timer = window.setInterval(refreshStates, 120000);
        return () => window.clearInterval(timer);
    }, [apiFetch, activeBookIds]);

    const handleSearch = async (event, options = {}) => {
        event?.preventDefault();
        const wantsFresh = options.fresh === true;
        if (wantsFresh && freshSecondsLeft > 0) return;
        setLoading(true);
        setError('');
        setNotice('');
        setAnalysisDone(false);
        const queryText = query.trim();
        const requestBase = {
            query: queryText,
            genres,
            maxResults: channelPreset === 'bkt' ? defaults.maxResults : Math.min(defaults.maxResults, 15),
            channelPreset,
            deepAnalysis: false,
            useWebSearch: true,
            includeAudioBb: userRole === 'admin',
            freshResearch: wantsFresh,
            discoveryMode
        };

        try {
            let payload;
            const similarQueries = wantsFresh && meta?.query === queryText && Array.isArray(meta?.similarQueries)
                ? meta.similarQueries.slice(0, 10)
                : [];
            const beginFreshCountdown = () => {
                if (!wantsFresh) return;
                setFreshReadyAt(Date.now() + freshCooldownSeconds * 1000);
                setClockNow(Date.now());
            };
            if (queryText) {
                beginFreshCountdown();
                setLoadingMessage(wantsFresh ? 'Đang đi tiếp dữ liệu mới và loại kết quả đã thấy…' : 'Đang tìm đồng thời trên các nguồn khả dụng…');
                payload = await apiFetch('/api/hopelab/research', {
                    method: 'POST',
                    body: JSON.stringify({
                        ...requestBase,
                        searchQueries: similarQueries,
                        generateSimilarInline: !similarQueries.length,
                        includeOriginalQuery: false
                    })
                });
            } else {
                beginFreshCountdown();
                setLoadingMessage(wantsFresh ? 'Đang tạo vòng dữ liệu research mới…' : 'Đang khám phá audiobook mới từ các thể loại đã chọn…');
                payload = await apiFetch('/api/hopelab/research', {
                    method: 'POST',
                    body: JSON.stringify(requestBase)
                });
            }

            const combinedBooks = payload.books || [];
            setShowSavedOnly(false);
            setBooks(combinedBooks);
            setCompareIds([]);
            setComparisonOpen(false);
            setMeta({ ...(payload.meta || {}), similarQueries: payload.meta?.similarQueries || similarQueries });
            setAiConfigured(Boolean(payload.meta?.aiConfigured ?? aiConfigured));
            setFreshReadyAt(payload.meta?.freshResearch?.nextAt ? Date.parse(payload.meta.freshResearch.nextAt) : 0);
            setFreshCooldownSeconds(Number(payload.meta?.freshResearch?.cooldownSeconds) || freshCooldownSeconds);
            setClockNow(Date.now());
            setLoading(false);
            setLoadingMessage('');

            const novelty = payload.meta?.novelty;
            setNotice(payload.meta?.dataWarning || (combinedBooks.length
                ? `Đã research ${combinedBooks.length} series · ${novelty?.newSeries ?? combinedBooks.length} series mới${novelty?.resurfacedSeries ? ` · ${novelty.resurfacedSeries} series được xem lại` : ''}. Chỉ bấm “Phân tích sâu” khi cần.`
                : 'Research hoàn tất nhưng chưa tìm thấy series mới phù hợp.'));
            if (payload.meta?.embedding?.pending && combinedBooks.length) {
                apiFetch('/api/hopelab/research', {
                    method: 'POST',
                    body: JSON.stringify({ action: 'semantic_rank', query: queryText, discoveryMode, books: combinedBooks })
                }).then(semanticPayload => {
                    if (Array.isArray(semanticPayload.books) && semanticPayload.books.length) setBooks(semanticPayload.books);
                    setMeta(current => ({ ...current, embedding: semanticPayload.embedding || current?.embedding }));
                }).catch(semanticError => {
                    setMeta(current => ({ ...current, embedding: { used: false, pending: false, model: current?.embedding?.model || '', error: semanticError.message } }));
                });
            }
        } catch (requestError) {
            if (wantsFresh && requestError.retryAfterSeconds) {
                setFreshReadyAt(Date.now() + requestError.retryAfterSeconds * 1000);
                setClockNow(Date.now());
            }
            if (userRole === 'admin' && books.length && /tạm thời|AudioBook/i.test(requestError.message)) {
                setNotice('Nguồn audiobook đang chập chờn. HopeLab giữ nguyên danh sách hiện tại để admin tiếp tục làm việc.');
            } else {
                setError(requestError.message);
            }
        } finally {
            setLoading(false);
            setLoadingMessage('');
        }
    };

    const handleAction = async (book, action, extra = {}) => {
        setBusyBookId(book.id);
        setError('');
        setNotice('');
        try {
            const payload = await apiFetch('/api/hopelab/action', {
                method: 'POST',
                body: JSON.stringify({ action, bookId: book.id, ...extra })
            });
            mergeState(payload.state);
            if (action === 'save') setSavedBooks(current => current.some(item => item.id === book.id) ? current.map(item => item.id === book.id ? { ...book, ...payload.state } : item) : [{ ...book, ...payload.state }, ...current]);
            if (action === 'unsave') setSavedBooks(current => current.filter(item => item.id !== book.id));
            setNotice(action === 'save' ? 'Đã thêm vào danh sách cá nhân.' : action === 'unsave' ? 'Đã bỏ khỏi danh sách cá nhân.' : action === 'claim' ? `Bạn đã nhận “${book.title}”.` : action === 'copyright' ? 'Đã ghi nhận cảnh báo bản quyền cho cả team.' : 'Đã cập nhật sách.');
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setBusyBookId('');
        }
    };

    const createTask = async taskData => {
        if (!taskBook) return;
        setTaskSaving(true);
        setError('');
        try {
            let seriesBooks = Array.isArray(taskBook.seriesBooks) ? taskBook.seriesBooks : [];
            if ((taskBook.seriesCount || 1) > 1 && !taskBook.fullSeriesLoaded) {
                try {
                    const seriesPayload = await apiFetch('/api/hopelab/research', {
                        method: 'POST',
                        body: JSON.stringify({
                            action: 'series_links',
                            seriesQuery: taskBook.seriesTitle || taskBook.title,
                            source: taskBook.abbUrl ? 'abb' : 'audiobb'
                        })
                    });
                    if (Array.isArray(seriesPayload.seriesBooks) && seriesPayload.seriesBooks.length) {
                        seriesBooks = seriesPayload.seriesBooks;
                    }
                } catch (_) {
                    // Existing grouped sources are still added when the full-series lookup is temporarily unavailable.
                }
            }
            const downloadBooks = seriesBooks.length ? seriesBooks : [taskBook];
            const seriesDownloads = downloadBooks.map((item, index) => ({
                title: item.title || 'Book ' + (index + 1),
                sources: [
                    item.abbUrl ? { type: 'abb', label: 'AudiobookBay', url: item.abbUrl } : null,
                    item.magnet ? { type: 'magnet', label: 'Magnet', url: item.magnet } : null,
                    ...(item.fileHostLinks || []).map(link => ({ type: 'file', label: link.label || 'File host', url: link.url }))
                ].filter(Boolean)
            })).filter(item => item.sources.length);
            const payload = await apiFetch('/api/hopelab/action', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'create_task',
                    bookId: taskBook.id,
                    bookTitle: taskBook.seriesTitle || taskBook.title,
                    seriesTitle: taskBook.seriesTitle || taskBook.title,
                    seriesDownloads,
                    link: taskBook.abbUrl || '',
                    ...taskData
                })
            });
            mergeState(payload.state);
            setTaskBook(null);
            setNotice('Đã tạo task cho ' + payload.state.taskAssignee + ' với nguồn tải của ' + Math.max(seriesDownloads.length, 1) + ' cuốn.');
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setTaskSaving(false);
        }
    };

    const applyChannelPreset = mode => {
        const currentYear = new Date().getFullYear();
        setChannelPreset(mode);
        setYearMin(defaults.yearMin);
        setYearMax(mode === 'farm' ? currentYear : Math.min(currentYear, defaults.yearMax));
        setStudioFilter('all');
        setHideBlacklisted(mode !== 'farm');
        setSortBy(mode === 'farm' ? 'farm-opportunity' : mode === 'bkt' ? 'bkt-opportunity' : 'opportunity');
        setIndependentOnly(false);
        setOlderThanFive(false);
        setCompletedOnly(false);
        setRatingMin(0);
        setReviewsMin(0);
        setReviewsMax(0);
    };

    const studios = useMemo(() => [...new Set(activeBooks.map(book => book.audioPublisher || book.printPublisher).filter(Boolean))].sort(), [activeBooks]);
    const bktPublisherStats = useMemo(() => activeBooks.reduce((stats, book) => {
        const status = book.publisherVerification?.status
            || (book.preferredStudio ? 'whitelist' : book.excludedStudio ? 'blacklist' : book.audioPublisher ? 'other' : 'unknown');
        if (Object.prototype.hasOwnProperty.call(stats, status)) stats[status] += 1;
        else stats.unknown += 1;
        return stats;
    }, { whitelist: 0, blacklist: 0, other: 0, unknown: 0 }), [activeBooks]);
    const visibleBooks = useMemo(() => {
        const filtered = activeBooks.filter(book => {
            if (channelPreset === 'bkt' && !book.preferredStudio) return false;
            if (hideBlacklisted && book.excludedStudio) return false;
            if (book.releaseYear && (book.releaseYear < Number(yearMin) || book.releaseYear > Number(yearMax))) return false;
            if (studioFilter !== 'all' && (book.audioPublisher || book.printPublisher) !== studioFilter) return false;
            if (independentOnly && !book.independentPublisher) return false;
            if (olderThanFive && (!book.releaseYear || book.releaseYear > new Date().getFullYear() - 5)) return false;
            if (completedOnly && !book.completedSeries) return false;
            if (Number(ratingMin) && (book.rating || 0) < Number(ratingMin)) return false;
            if (Number(reviewsMin) && (book.ratingsCount || 0) < Number(reviewsMin)) return false;
            if (Number(reviewsMax) && (book.ratingsCount || 0) > Number(reviewsMax)) return false;
            return true;
        });
        return [...filtered].sort((a, b) => {
            if (sortBy === 'year-desc') return (b.releaseYear || 0) - (a.releaseYear || 0);
            if (sortBy === 'year-asc') return (a.releaseYear || 9999) - (b.releaseYear || 9999);
            if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
            if (sortBy === 'interest') return (b.interestScore || 0) - (a.interestScore || 0);
            if (sortBy === 'opportunity') return (b.opportunityScore || 0) - (a.opportunityScore || 0);
            if (sortBy === 'bkt-opportunity') return (b.bktOpportunityScore || 0) - (a.bktOpportunityScore || 0);
            if (sortBy === 'farm-opportunity') return (b.farmOpportunityScore || 0) - (a.farmOpportunityScore || 0);
            if (sortBy === 'semantic') return (b.semanticSimilarity || 0) - (a.semanticSimilarity || 0);
            if (sortBy === 'competition') return (a.competitionScore || 0) - (b.competitionScore || 0);
            return (b.score || 0) - (a.score || 0);
        });
    }, [activeBooks, channelPreset, completedOnly, hideBlacklisted, independentOnly, olderThanFive, ratingMin, reviewsMax, reviewsMin, sortBy, studioFilter, yearMax, yearMin]);

    const selectGenre = genre => setGenres([genre]);
    const seriesModalBook = seriesModalId ? activeBooks.find(item => item.id === seriesModalId) : null;
    const compareBooks = compareIds.map(id => activeBooks.find(book => book.id === id)).filter(Boolean);
    const toggleCompare = id => setCompareIds(current => current.includes(id) ? current.filter(item => item !== id) : current.length < 4 ? [...current, id] : current);
    const applyCopilotFilters = filters => {
        if (filters.searchQuery) setQuery(String(filters.searchQuery));
        if (filters.discoveryMode) setDiscoveryMode(String(filters.discoveryMode));
        if (filters.yearMin) setYearMin(Number(filters.yearMin));
        if (filters.yearMax) setYearMax(Number(filters.yearMax));
        if (filters.olderThanYears) setOlderThanFive(true);
        if (filters.independentOnly) setIndependentOnly(true);
        if (filters.completedSeries) setCompletedOnly(true);
        if (filters.ratingMin) setRatingMin(Number(filters.ratingMin));
        if (filters.reviewsMin) setReviewsMin(Number(filters.reviewsMin));
        if (filters.reviewsMax) setReviewsMax(Number(filters.reviewsMax));
        if (Array.isArray(filters.excludePublishers) && filters.excludePublishers.length) setHideBlacklisted(true);
        setShowFilters(true);
        setCopilotOpen(false);
        setNotice('Copilot đã áp dụng bộ lọc. Bấm Research sách để lấy dữ liệu mới nếu cần.');
    };

    const handlePublisherVerification = async () => {
        const candidates = activeBooks.filter(book => {
            const status = book.publisherVerification?.status
                || (book.preferredStudio ? 'whitelist' : book.excludedStudio ? 'blacklist' : book.audioPublisher ? 'other' : 'unknown');
            return status === 'unknown';
        });
        if (!candidates.length || verifyingPublishers) return;
        setVerifyingPublishers(true);
        setError('');
        setNotice(`Đang xác minh audio publisher cho ${candidates.length} series theo từng nhóm nhỏ…`);
        const chunks = [];
        for (let index = 0; index < candidates.length; index += 4) chunks.push(candidates.slice(index, index + 4));
        const errors = [];
        let processed = 0;
        try {
            for (const chunk of chunks) {
                try {
                    const payload = await apiFetch('/api/hopelab/research', {
                        method: 'POST',
                        body: JSON.stringify({
                            action: 'verify_publishers',
                            books: chunk,
                            queryContext: `${query.trim() || genres[0]} · Chỉ xác minh audio publisher cho Kênh BKT: ${defaults.preferredStudios.join(', ')}`,
                            useWebSearch: true
                        })
                    });
                    const updates = new Map((payload.books || []).map(book => [book.id, book]));
                    setBooks(current => current.map(book => updates.has(book.id) ? { ...book, ...updates.get(book.id) } : book));
                    processed += updates.size;
                    if (payload.aiError) errors.push(payload.aiError);
                } catch (requestError) {
                    errors.push(requestError.message);
                }
            }
            setNotice(errors.length
                ? `Đã xác minh ${processed}/${candidates.length} series; một số nhóm cần thử lại: ${[...new Set(errors)].join(' · ')}`
                : `Đã xác minh publisher cho ${processed} series. Kết quả BKT đã được cập nhật.`);
        } finally {
            setVerifyingPublishers(false);
        }
    };

    const handleDeepAnalysis = async () => {
        const candidates = visibleBooks;
        if (!candidates.length || analyzing) return;
        if (!aiConfigured) {
            setError('Admin chưa cấu hình AI cho HopeLab.');
            return;
        }
        setAnalyzing(true);
        setError('');
        setNotice(`AI đang phân tích sâu ${candidates.length} series đang hiển thị theo từng nhóm nhỏ…`);
        const chunks = [];
        for (let index = 0; index < candidates.length; index += 4) chunks.push(candidates.slice(index, index + 4));
        const aiErrors = [];
        try {
            for (let index = 0; index < chunks.length; index += 2) {
                const pair = chunks.slice(index, index + 2);
                const results = await Promise.all(pair.map(async chunk => {
                    const request = () => apiFetch('/api/hopelab/research', {
                        method: 'POST',
                        body: JSON.stringify({
                            action: 'analyze_batch',
                            books: chunk,
                            queryContext: channelPreset === 'bkt'
                                ? `${query.trim() || genres[0]} · Kênh BKT chỉ dùng audio publisher whitelist: ${defaults.preferredStudios.join(', ')}`
                                : query.trim() || genres[0],
                            useWebSearch: true
                        })
                    });
                    try {
                        return await request();
                    } catch (_) {
                        try { return await request(); }
                        catch (retryError) { return { books: [], aiError: retryError.message }; }
                    }
                }));
                results.forEach(result => {
                    if (result.aiError) aiErrors.push(result.aiError);
                    if (result.books?.length) {
                        const updates = new Map(result.books.map(book => [book.id, book]));
                        setBooks(current => current.map(book => updates.has(book.id) ? { ...book, ...updates.get(book.id) } : book));
                    }
                });
            }
            setAnalysisDone(true);
            setNotice(aiErrors.length
                ? `Đã phân tích xong; một số nhóm tạm lỗi: ${[...new Set(aiErrors)].join(' · ')}`
                : `Đã phân tích sâu ${candidates.length} series.`);
        } finally {
            setAnalyzing(false);
        }
    };

    const loadFullSeriesLinks = async book => {
        setLoadingSeriesId(book.id);
        setSeriesError('');
        try {
            const payload = await apiFetch('/api/hopelab/research', {
                method: 'POST',
                body: JSON.stringify({ action: 'series_links', seriesQuery: book.seriesTitle || book.title, source: book.abbUrl ? 'abb' : 'audiobb' })
            });
            const seriesBooks = payload.seriesBooks || [];
            setBooks(current => current.map(item => item.id === book.id
                ? { ...item, seriesBooks, seriesCount: seriesBooks.length, fullSeriesLoaded: true }
                : item));
            if (seriesBooks.length) setNotice(`Đã tải ${seriesBooks.length} volume và link của series.`);
            else setSeriesError('Chưa tìm thấy volume nào cho series này. Hãy thử lại sau.');
        } catch (requestError) {
            setSeriesError(requestError.message);
        } finally {
            setLoadingSeriesId('');
        }
    };

    const copySeriesMagnets = async book => {
        const magnets = [...new Set((book.seriesBooks || []).map(item => item.magnet).filter(Boolean))];
        if (!magnets.length) {
            setNotice('Series này chưa có magnet được xác minh.');
            return;
        }
        await navigator.clipboard.writeText(magnets.join('\n'));
        setNotice(`Đã sao chép ${magnets.length} magnet của series. Có thể dán toàn bộ vào qBittorrent.`);
    };

    const copyOneMagnet = async item => {
        if (!item.magnet) {
            setNotice('Cuốn này chưa có magnet được xác minh.');
            return;
        }
        await navigator.clipboard.writeText(item.magnet);
        setNotice(`Đã sao chép magnet: ${item.title}`);
    };

    const downloadSeriesLinks = book => {
        const lines = (book.seriesBooks || []).flatMap((item, index) => [
            `${index + 1}. ${item.title}`,
            `AudiobookBay: ${item.abbUrl || ''}`,
            ...(userRole === 'admin' ? [`AudioBB: ${item.audiobbUrl || ''}`, ...(item.fileHostLinks || []).map(link => `${link.label}: ${link.url}`)] : []),
            `Magnet: ${item.magnet || 'Chưa xác minh'}`,
            ''
        ]);
        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${(book.seriesTitle || book.title).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'series'}-links.txt`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="hopelab-page">
            <section className="hopelab-hero">
                <div className="hopelab-hero-orb hopelab-hero-orb-one" />
                <div className="hopelab-hero-orb hopelab-hero-orb-two" />
                <div className="hopelab-hero-copy">
                    <div className="hopelab-eyebrow"><Sparkles size={13} /> HOPELAB · AUDIOBOOK DISCOVERY</div>
                    <h1>Sách mới,<br /><em>Doanh thu mới.</em></h1>
                    <p>Hỗ trợ team trong việc tìm kiếm những mỏ vàng mới cho audiobook.</p>
                    <div className="hopelab-hero-statuses">
                        <span className={sourceStatus.abb?.live ? 'is-online' : 'is-offline'} title={sourceStatus.abb?.checkedAt ? `Kiểm tra: ${new Date(sourceStatus.abb.checkedAt).toLocaleString('vi-VN')}` : ''}>
                            {sourceStatus.abb?.live ? <Check size={12} /> : <AlertTriangle size={12} />} {sourceStatus.abb?.live ? 'ABB hoạt động' : 'ABB tạm offline'}
                        </span>
                        {userRole === 'admin' && <span className={sourceStatus.audiobb?.live ? 'is-online' : 'is-offline'} title={sourceStatus.audiobb?.checkedAt ? `Kiểm tra: ${new Date(sourceStatus.audiobb.checkedAt).toLocaleString('vi-VN')}` : ''}>
                            {sourceStatus.audiobb?.live ? <Check size={12} /> : <AlertTriangle size={12} />} {sourceStatus.audiobb?.live ? 'AudioBB hoạt động · Admin' : 'AudioBB offline · Admin'}
                        </span>}
                        <span className={aiConfigured ? 'is-online' : 'is-offline'}><Bot size={12} /> {aiConfigured ? model : 'AI chưa cấu hình'}</span>
                        <span><RefreshCw size={12} /> Làm mới {defaults.randomRotationHours || 12}h</span>
                    </div>
                </div>
                <div className="hopelab-hero-metric">
                    <div className="hopelab-metric-heading">
                        <div><span>PHẠM VI TÌM SÁCH</span><small>Thiết lập sẵn cho team</small></div>
                        <strong>{defaults.yearMin}—{defaults.yearMax}</strong>
                    </div>
                    <div className="hopelab-metric-grid">
                        <div><span>Tìm sách từ</span><b>{userRole === 'admin' ? 'ABB + AudioBB' : 'AudioBookBay'}</b></div>
                        <div><span>Kiểm tra thêm</span><b>Nguồn web</b></div>
                        <div><span>AI phân tích</span><b>Khi bạn yêu cầu</b></div>
                    </div>
                    <div className="hopelab-metric-note"><Check size={12} /><span>Tự ưu tiên publisher phù hợp và cảnh báo publisher rủi ro</span></div>
                </div>
            </section>

            <section className="hopelab-search-panel">
                <DiscoveryModes value={discoveryMode} onChange={mode => { setDiscoveryMode(mode); if (['litrpg', 'sci-fi'].includes(mode)) setGenres([mode === 'litrpg' ? 'LitRPG' : 'Sci-Fi']); }} />
                <form onSubmit={handleSearch}>
                    <div className="hopelab-search-main">
                        <Search size={21} />
                        <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Dùng một cuốn làm sách mồi để khám phá series khác, hoặc để trống để đào mỏ vàng…" />
                        {query && <button type="button" className="hopelab-clear-query" onClick={() => setQuery('')}><X size={16} /></button>}
                        <button type="submit" className="hopelab-search-button" disabled={loading || !genres.length}>
                            {loading ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
                            {loading ? 'Đang khám phá…' : (query.trim() ? 'Khám phá sách tương tự' : 'Khám phá mỏ vàng')}
                        </button>
                        <button
                            type="button"
                            className="hopelab-fresh-button"
                            disabled={loading || !genres.length || freshSecondsLeft > 0}
                            title={freshSecondsLeft ? `Có thể Fresh lại sau ${freshCountdown}` : 'Tạo một vòng dữ liệu research mới'}
                            onClick={() => handleSearch(null, { fresh: true })}
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                            {freshSecondsLeft ? `Fresh sau ${freshCountdown}` : 'Fresh research'}
                        </button>
                        <button type="button" className={`hopelab-saved-library-button ${showSavedOnly ? 'is-active' : ''}`} onClick={() => setShowSavedOnly(current => !current)}>
                            <Bookmark size={16} fill={showSavedOnly ? 'currentColor' : 'none'} />
                            Danh sách của tôi ({savedBooks.length})
                        </button>
                    </div>
                    <div className="hopelab-genre-row">
                        <span>Nhóm kênh:</span>
                        {defaults.genres.map(genre => (
                            <button key={genre} type="button" className={genres.includes(genre) ? 'is-active' : ''} onClick={() => selectGenre(genre)}>{genre}</button>
                        ))}
                        <span className="hopelab-target-label">Mục tiêu:</span>
                        <button type="button" className={channelPreset === 'standard' ? 'is-target-active' : ''} onClick={() => applyChannelPreset('standard')}>Tiêu chuẩn</button>
                        <button type="button" className={channelPreset === 'bkt' ? 'is-target-active' : ''} onClick={() => applyChannelPreset('bkt')}>Kênh BKT</button>
                        <button type="button" className={channelPreset === 'farm' ? 'is-target-active' : ''} onClick={() => applyChannelPreset('farm')}>Kênh nuôi</button>
                        <button type="button" className="hopelab-filter-trigger" onClick={() => setShowFilters(value => !value)}><Filter size={14} /> Bộ lọc <ChevronDown size={13} /></button>
                    </div>
                </form>

                {showFilters && (
                    <div className="hopelab-filter-drawer">
                        <label><span>Từ năm</span><input type="number" min="1900" max={new Date().getFullYear()} value={yearMin} onChange={event => setYearMin(Math.min(new Date().getFullYear(), Number(event.target.value)))} /></label>
                        <label><span>Đến năm</span><input type="number" min="1900" max={new Date().getFullYear()} value={yearMax} onChange={event => setYearMax(Math.min(new Date().getFullYear(), Number(event.target.value)))} /></label>
                        <label><span>Audio publisher</span><select value={studioFilter} onChange={event => setStudioFilter(event.target.value)}><option value="all">Tất cả publisher</option>{studios.map(studio => <option key={studio} value={studio}>{studio}</option>)}</select></label>
                        <label><span>Sắp xếp</span><select value={sortBy} onChange={event => setSortBy(event.target.value)}><option value="score">Điểm đề xuất</option><option value="opportunity">Opportunity Score</option><option value="bkt-opportunity">Cơ hội Kênh BKT</option><option value="farm-opportunity">Cơ hội Kênh nuôi</option><option value="semantic">Độ tương tự nội dung</option><option value="competition">Cạnh tranh thấp</option><option value="interest">Mức quan tâm</option><option value="rating">Rating</option><option value="year-desc">Năm mới → cũ</option><option value="year-asc">Năm cũ → mới</option></select></label>
                        <label><span>Rating tối thiểu</span><input type="number" min="0" max="5" step="0.1" value={ratingMin} onChange={event => setRatingMin(event.target.value)} /></label>
                        <label><span>Reviews từ</span><input type="number" min="0" value={reviewsMin} onChange={event => setReviewsMin(event.target.value)} /></label>
                        <label><span>Reviews đến</span><input type="number" min="0" value={reviewsMax} onChange={event => setReviewsMax(event.target.value)} placeholder="Không giới hạn" /></label>
                        <label className="hopelab-checkbox"><input type="checkbox" checked={independentOnly} onChange={event => setIndependentOnly(event.target.checked)} /><span>Chỉ publisher độc lập</span></label>
                        <label className="hopelab-checkbox"><input type="checkbox" checked={olderThanFive} onChange={event => setOlderThanFive(event.target.checked)} /><span>Chỉ sách cũ hơn 5 năm</span></label>
                        <label className="hopelab-checkbox"><input type="checkbox" checked={completedOnly} onChange={event => setCompletedOnly(event.target.checked)} /><span>Series đã hoàn thành</span></label>
                        <label className="hopelab-checkbox"><input type="checkbox" checked={hideBlacklisted} disabled={channelPreset === 'bkt'} onChange={event => setHideBlacklisted(event.target.checked)} /><span>{channelPreset === 'bkt' ? 'Chỉ publisher whitelist' : 'Ẩn publisher blacklist'}</span></label>
                        <button type="button" onClick={() => applyChannelPreset('standard')}>Khôi phục mặc định</button>
                    </div>
                )}
            </section>

            {loading && <div className="hopelab-loading-state"><div className="hopelab-loader"><span /><span /><span /></div><strong>{loadingMessage}</strong><p>HopeLab chỉ dùng AI sau khi đã có dữ liệu thực để đối chiếu.</p></div>}
            {error && <div className="hopelab-alert is-error"><AlertOctagon size={18} /><span>{error}</span><button onClick={() => setError('')}><X size={16} /></button></div>}
            {notice && <div className="hopelab-alert is-notice"><Check size={18} /><span>{notice}</span><button onClick={() => setNotice('')}><X size={16} /></button></div>}
            {userRole === 'admin' && meta?.sourceDiagnostics && (
                <div className="hopelab-source-diagnostics">
                    <div><strong>Nguồn của lượt research này</strong><span>Chi tiết này chỉ admin nhìn thấy</span></div>
                    {Object.entries(meta.sourceDiagnostics).map(([source, info]) => (
                        <div key={source} className={`hopelab-source-diagnostic is-${info.status}`}>
                            <span>{source === 'abb' ? 'AudiobookBay' : 'AudioBB'}</span>
                            <strong>{info.status === 'ok' ? `${info.resultCount} kết quả` : info.status === 'skipped' ? 'Đã bỏ qua' : 'Không phản hồi'}</strong>
                            <small>{info.durationMs ? `${(info.durationMs / 1000).toFixed(1)} giây` : info.message}</small>
                        </div>
                    ))}
                </div>
            )}

            {!loading && meta && <InsightsBar insights={meta.insights || []} embedding={meta.embedding} />}

            {!loading && meta && (
                <div className="hopelab-results-toolbar">
                    <div><span>SHORTLIST</span><strong>{visibleBooks.length} / {books.length} sách</strong><small>{channelPreset === 'farm' ? 'Kênh nuôi · có blacklist · ưu tiên sách mới' : channelPreset === 'bkt' ? 'Kênh BKT · whitelist only' : 'Preset chuẩn'}</small><small>Cập nhật {new Date(meta.generatedAt).toLocaleString('vi-VN')}</small></div>
                    <div className="hopelab-results-tools">
                        <button type="button" className="hopelab-copilot-button" onClick={() => setCopilotOpen(true)}><Bot size={15} /> Hỏi Copilot</button>
                        {channelPreset === 'bkt' && bktPublisherStats.unknown > 0 && (
                            <button type="button" className="hopelab-publisher-verify-button" disabled={verifyingPublishers} onClick={handlePublisherVerification}>
                                {verifyingPublishers ? <Loader2 size={15} className="animate-spin" /> : <ShieldAlert size={15} />}
                                {verifyingPublishers ? 'Đang xác minh publisher?' : `Xác minh ${bktPublisherStats.unknown} publisher`}
                            </button>
                        )}
                        <button type="button" className="hopelab-deep-analysis-button" disabled={analyzing || !visibleBooks.length || !aiConfigured} onClick={handleDeepAnalysis}>
                            {analyzing ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
                            {analyzing ? 'Đang phân tích…' : analysisDone ? `Phân tích lại ${visibleBooks.length} sách` : `Phân tích sâu ${visibleBooks.length} sách`}
                        </button>
                        <div className="hopelab-results-legend"><span><i className="is-good" /> Whitelist</span><span><i className="is-risk" /> Copyright/blacklist</span><span><i className="is-claimed" /> Đã có người nhận</span></div>
                    </div>
                </div>
            )}

            {!loading && !activeBooks.length && (
                <section className="hopelab-empty-state">
                    <div className="hopelab-empty-icon"><Globe2 size={34} /></div>
                    <h2>Bắt đầu từ một cuốn sách hoặc để HopeLab tự khám phá</h2>
                    <p>Nhập tên cuốn đang có view tốt để tìm sách tương tự. Để trống nếu muốn lấy một vòng trending ngẫu nhiên từ sáu genre chính.</p>
                    <button onClick={handleSearch}><Flame size={16} /> Khám phá danh sách mới</button>
                </section>
            )}

            {!loading && activeBooks.length > 0 && visibleBooks.length === 0 && (
                <section className="hopelab-empty-state hopelab-filtered-empty">
                    <div className="hopelab-empty-icon"><ShieldAlert size={34} /></div>
                    <h2>Chưa có kết quả đủ điều kiện Kênh BKT</h2>
                    <p>HopeLab vẫn giữ đủ {books.length} series. Chỉ audio publisher có nguồn xác minh và thuộc whitelist mới được đề xuất cho BKT.</p>
                    <div className="hopelab-bkt-publisher-stats">
                        <div className="is-good"><strong>{bktPublisherStats.whitelist}</strong><span>Đủ whitelist</span></div>
                        <div><strong>{bktPublisherStats.other}</strong><span>Ngoài whitelist</span></div>
                        <div className="is-risk"><strong>{bktPublisherStats.blacklist}</strong><span>Thuộc blacklist</span></div>
                        <div className="is-pending"><strong>{bktPublisherStats.unknown}</strong><span>Chưa xác minh</span></div>
                    </div>
                    <div className="hopelab-empty-actions">
                        {bktPublisherStats.unknown > 0 && (
                            <button type="button" disabled={verifyingPublishers} onClick={handlePublisherVerification}>
                                {verifyingPublishers ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                                {verifyingPublishers ? 'Đang kiểm tra nguồn?' : `Xác minh ${bktPublisherStats.unknown} publisher còn thiếu`}
                            </button>
                        )}
                        <button type="button" className="is-secondary" onClick={() => applyChannelPreset('standard')}>Xem {books.length} kết quả tiêu chuẩn</button>
                    </div>
                </section>
            )}

            <section className="hopelab-results-grid">
                {visibleBooks.map(book => {
                    const studio = book.audioPublisher || book.printPublisher || 'Chưa xác minh publisher';
                    const claimedBy = book.claimedBy;
                    const copyrightFlag = Boolean(book.copyrightStatus);
                    const busy = busyBookId === book.id;
                    return (
                        <article key={book.id} className={`hopelab-book-card ${book.excludedStudio ? 'is-blacklisted' : ''} ${copyrightFlag ? 'is-copyright' : ''}`}>
                            <div className="hopelab-book-cover">
                                <button type="button" className={`hopelab-compare-toggle ${compareIds.includes(book.id) ? 'is-selected' : ''}`} onClick={() => toggleCompare(book.id)} disabled={!compareIds.includes(book.id) && compareIds.length >= 4} title="Chọn để so sánh">{compareIds.includes(book.id) ? <Check size={13} /> : '+'}</button>
                                <SmartBookCover cover={book.cover} title={book.title} />
                                <ScoreRing score={book.score} />
                                {book.preferredStudio && <span className="hopelab-cover-badge is-whitelist"><Check size={11} /> Whitelist</span>}
                                {book.excludedStudio && <span className="hopelab-cover-badge is-blacklist"><ShieldAlert size={11} /> Blacklist</span>}
                            </div>
                            <div className="hopelab-book-body">
                                <div className="hopelab-book-kicker">
                                    <span className="hopelab-series-source">{book.sourceLabel}{book.seriesCount > 1 && <b>{book.seriesCount} volume đã gom</b>}{book.previouslySeen && <b className="is-seen">Đã từng xuất hiện</b>}</span>
                                    <span className={book.interestScore >= 70 ? 'is-hot' : ''}><Flame size={12} /> {book.interestLabel}</span>
                                </div>
                                <h2>{book.seriesTitle || book.title}</h2>
                                <p className="hopelab-book-author">{book.seriesTitle && <span>Đại diện: {book.title} · </span>}{book.author || 'Chưa xác định tác giả'} {book.narrator && <span>· Narrator: {book.narrator}</span>}</p>

                                <div className="hopelab-book-facts">
                                    <div><span>Năm {book.releaseYear && <em>{book.releaseYearSource || 'Google Books'}</em>}</span><strong>{book.releaseYear || 'N/A'}</strong></div>
                                    <div><span>Rating</span><strong><Star size={13} /> {book.rating ? `${book.rating.toFixed(1)} (${book.ratingsCount || 0})` : 'Chưa có'}</strong></div>
                                    <div><span>{book.audioPublisher ? 'Audio publisher' : book.printPublisher ? 'Print publisher' : 'Publisher'} {(book.audioPublisher || book.printPublisher) && <em>{book.audioPublisher ? 'AudioBookBay' : 'Google Books'}</em>}</span><strong title={studio}>{studio}</strong></div>
                                    <div><span>ABB</span><strong>{book.abbPostedAt || 'N/A'}</strong></div>
                                </div>
                                <div className="hopelab-opportunity-row">
                                    <div><span>Opportunity</span><strong>{book.opportunityScore || 0}</strong></div>
                                    <div><span>Kênh BKT</span><strong>{book.bktOpportunityScore || 0}</strong></div>
                                    <div><span>Kênh nuôi</span><strong>{book.farmOpportunityScore || 0}</strong></div>
                                    <div><span>Semantic</span><strong>{book.semanticSimilarity != null ? book.semanticSimilarity : '—'}</strong></div>
                                    <div><span>Độ tin cậy</span><strong>{book.dataConfidence || 0}%</strong></div>
                                </div>

                                {copyrightFlag && (
                                    <div className="hopelab-copyright-warning">
                                        <AlertTriangle size={17} />
                                        <div><strong>Đã bị team đánh dấu bản quyền</strong><span>{book.copyrightReport?.note || 'Không nên tải hoặc chọn lại.'}</span></div>
                                    </div>
                                )}

                                <div className="hopelab-analysis">
                                    <div className="hopelab-analysis-title"><Bot size={15} /> Vì sao nên cân nhắc</div>
                                    <ul>{(book.aiAnalysis?.evidence?.length ? book.aiAnalysis.evidence : book.reasons || []).slice(0, 4).map((reason, index) => <li key={index}>{reason}</li>)}</ul>
                                    {book.aiAnalysis?.summary && <p>{book.aiAnalysis.summary}</p>}
                                    {(book.aiAnalysis?.caveats || book.warnings || []).length > 0 && (
                                        <details><summary>Điểm cần lưu ý</summary><ul>{(book.aiAnalysis?.caveats?.length ? book.aiAnalysis.caveats : book.warnings).slice(0, 4).map((warning, index) => <li key={index}>{warning}</li>)}</ul></details>
                                    )}
                                    {book.aiAnalysis?.sources?.length > 0 && <div className="hopelab-sources">{book.aiAnalysis.sources.slice(0, 3).map((source, index) => <a key={index} href={source.url} target="_blank" rel="noreferrer"><Globe2 size={11} /> {source.title || `Nguồn ${index + 1}`}</a>)}</div>}
                                </div>

                                {book.seriesCount > 1 && (
                                    <button type="button" className="hopelab-series-open-button" onClick={() => {
                                        setSeriesError('');
                                        setSeriesModalId(book.id);
                                        if (!book.fullSeriesLoaded) loadFullSeriesLinks(book);
                                    }}>
                                        <BookOpen size={15} />
                                        <span><strong>Xem toàn bộ series</strong><small>{book.fullSeriesLoaded ? `${book.seriesCount} volume đã xác minh` : `${book.seriesCount} volume đã gom · bấm để lấy full list`}</small></span>
                                        <ArrowRight size={15} />
                                    </button>
                                )}

                                <div className="hopelab-book-state">
                                    {claimedBy ? <><Avatar person={claimedBy} size="sm" /><div><span>Đã được chọn bởi</span><strong>{claimedBy.name}</strong><small>{timestampLabel(book.claimedAt)}</small></div></> : <><span className="hopelab-unclaimed-icon"><UserCheck size={16} /></span><div><span>Trạng thái</span><strong>Chưa ai nhận</strong></div></>}
                                    {book.taskId && <button onClick={() => onNavigate?.('tasks')}><ClipboardCheck size={13} /> Task: {book.taskAssignee || 'đã tạo'} <ArrowRight size={13} /></button>}
                                </div>

                                <div className="hopelab-book-links">
                                    {book.abbUrl && <a href={book.abbUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Mở AudiobookBay</a>}
                                    {userRole === 'admin' && book.audiobbUrl && <a href={book.audiobbUrl} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Mở AudioBB</a>}
                                    {userRole === 'admin' && (book.fileHostLinks || []).map(link => <a key={link.url} href={link.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Mở {link.label}</a>)}
                                    {book.magnet ? <><a href={book.magnet} title="Mở bằng ứng dụng torrent mặc định trên máy"><Magnet size={14} /> Mở bằng app torrent</a><button title="Sao chép magnet dự phòng" onClick={async () => { await navigator.clipboard.writeText(book.magnet); setNotice('Đã sao chép magnet vào clipboard.'); }}><Copy size={14} /> Sao chép magnet</button></> : <span><Magnet size={14} /> Chưa có magnet</span>}
                                    {book.googleBooksUrl && <a href={book.googleBooksUrl} target="_blank" rel="noreferrer"><BookOpen size={14} /> Metadata</a>}
                                </div>

                                <div className="hopelab-book-actions">
                                    <button disabled={busy} className={`hopelab-icon-button is-save ${book.saved ? 'is-active' : ''}`} title={book.saved ? 'Bỏ khỏi danh sách của tôi' : 'Lưu vào danh sách của tôi'} onClick={() => handleAction(book, book.saved ? 'unsave' : 'save')}>
                                        <Bookmark size={16} fill={book.saved ? 'currentColor' : 'none'} />
                                    </button>
                                    {!claimedBy ? (
                                        <button disabled={busy || copyrightFlag} className="hopelab-secondary-button" onClick={() => handleAction(book, 'claim')}>{busy ? <Loader2 size={15} className="animate-spin" /> : <UserCheck size={15} />} Nhận sách</button>
                                    ) : claimedBy.staffId === currentUserData?.id && !book.taskId ? (
                                        <button disabled={busy} className="hopelab-secondary-button" onClick={() => handleAction(book, 'release')}>Bỏ nhận</button>
                                    ) : <span className="hopelab-action-spacer" />}
                                    {!copyrightFlag ? (
                                        <button disabled={busy} className="hopelab-icon-button is-danger" title="Đánh dấu bản quyền" onClick={() => {
                                            const note = window.prompt('Lý do/case bản quyền để team biết:', 'Đã bị claim hoặc publisher thường xuyên đánh bản quyền');
                                            if (note !== null) handleAction(book, 'copyright', { note });
                                        }}><ShieldAlert size={16} /></button>
                                    ) : ['admin', 'staff'].includes(userRole) && (
                                        <button disabled={busy} className="hopelab-icon-button" title="Gỡ cờ bản quyền" onClick={() => handleAction(book, 'clear_copyright')}><RefreshCw size={15} /></button>
                                    )}
                                    <button disabled={Boolean(book.taskId) || copyrightFlag} className="hopelab-primary-button" onClick={() => setTaskBook(book)}><CalendarDays size={15} /> {book.taskId ? 'Đã có task' : 'Tạo task'}</button>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </section>

            <CompareFloatingButton count={compareIds.length} onClick={() => setComparisonOpen(true)} />
            {comparisonOpen && compareBooks.length > 0 && <ComparisonModal books={compareBooks} onClose={() => setComparisonOpen(false)} onRemove={id => setCompareIds(current => current.filter(item => item !== id))} />}
            <CopilotDrawer open={copilotOpen} onClose={() => setCopilotOpen(false)} apiFetch={apiFetch} books={visibleBooks} onApplyFilters={applyCopilotFilters} />

            {taskBook && <TaskModal book={taskBook} role={userRole} currentUserData={currentUserData} staff={staff} channels={channels} tasks={tasks} onClose={() => setTaskBook(null)} onCreate={createTask} saving={taskSaving} />}
            {seriesModalBook && (
                <SeriesModal
                    book={seriesModalBook}
                    loading={loadingSeriesId === seriesModalBook.id}
                    error={seriesError}
                    onClose={() => { setSeriesModalId(''); setSeriesError(''); }}
                    onRefresh={() => loadFullSeriesLinks(seriesModalBook)}
                    onCopyAll={() => copySeriesMagnets(seriesModalBook)}
                    onDownload={() => downloadSeriesLinks(seriesModalBook)}
                    onCopyOne={copyOneMagnet}
                    isAdmin={userRole === 'admin'}
                />
            )}
        </div>
    );
}
